import type { ActivationPendingUserResponse } from '@acme/api/admin';
import {
  UserService as AdminUserService,
  UserStatusEnum,
} from '@acme/api/admin';
import { Icon } from '@acme/assets';
import { UsersTable } from '@acme/features';
import { FilterDropdown, Input, useModal } from '@acme/foundation';
import { Alert } from 'flowbite-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const STATUS_OPTIONS = [
  { value: undefined, label: 'Все' },
  {
    value: UserStatusEnum.AwaitingСonfirmation,
    label: 'Ожидает подтверждения',
  },
  { value: UserStatusEnum.ExpectsChanges, label: 'Ожидает изменений' },
  { value: UserStatusEnum.Confirmed, label: 'Подтвержден' },
  { value: UserStatusEnum.Blocked, label: 'Заблокирован' },
];

function filterUsers(
  users: ActivationPendingUserResponse[],
  searchQuery: string,
  statusFilter: UserStatusEnum | undefined
): ActivationPendingUserResponse[] {
  let result = users;

  const query = searchQuery.trim().toLowerCase();
  if (query) {
    result = result.filter((user) => {
      const name = (user.name ?? '').toLowerCase();
      const org = (user.organizationName ?? '').toLowerCase();
      const desc = (user.transitionDescription ?? '').toLowerCase();
      return (
        name.includes(query) || org.includes(query) || desc.includes(query)
      );
    });
  }

  if (statusFilter !== undefined) {
    result = result.filter((user) => user.status === statusFilter);
  }

  return result;
}

export function UsersPendingPage() {
  const navigate = useNavigate();
  const [users, setUsers] = useState<ActivationPendingUserResponse[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<UserStatusEnum | undefined>(
    undefined
  );
  const { openConfirmModal } = useModal();

  const filteredUsers = useMemo(
    () => filterUsers(users, searchQuery, statusFilter),
    [users, searchQuery, statusFilter]
  );

  const fetchUsers = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);

      const response = await AdminUserService.getApiAdminV1UserPending();

      if (response.data) {
        setUsers(response.data);
      } else {
        setUsers([]);
      }
    } catch (error) {
      console.error('Ошибка загрузки пользователей:', error);
      setError('Ошибка загрузки пользователей. Попробуйте обновить страницу.');
      setUsers([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleRowClick = (userId: string) => {
    const selected = users.find((u) => u.id === userId);
    navigate(`/users/${userId}`, {
      state: {
        user: selected,
      },
    });
  };

  const handleApprove = async (userId: string) => {
    try {
      const confirmed = await openConfirmModal({
        title: 'Подтверждение активации',
        content: 'Вы уверены, что хотите активировать этого пользователя?',
        okBtn: 'Активировать',
        cancelBtn: 'Отмена',
      });

      if (!confirmed) {
        return;
      }

      await AdminUserService.putApiAdminV1UserActivate({ id: userId });
      await fetchUsers();
    } catch (error) {
      console.error('Ошибка при одобрении пользователя:', error);
      setError('Ошибка при активации пользователя. Попробуйте еще раз.');
    }
  };

  const handleReject = async (userId: string) => {
    try {
      const confirmed = await openConfirmModal({
        title: 'Отправка на доработку',
        content:
          'Вы уверены, что хотите отправить этого пользователя на доработку?',
        okBtn: 'Отправить на доработку',
        cancelBtn: 'Отмена',
      });

      if (!confirmed) {
        return;
      }

      await AdminUserService.putApiAdminV1UserRework({ id: userId });
      await fetchUsers();
    } catch (error) {
      console.error('Ошибка при отклонении пользователя:', error);
      setError('Ошибка при отправке на доработку. Попробуйте еще раз.');
    }
  };

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-2">
        <h1 className="text-2xl font-semibold text-gray-900">Пользователи</h1>
      </div>

      {error && (
        <Alert color="failure">
          <span className="font-medium">Ошибка!</span> {error}
        </Alert>
      )}

      <div className="flex flex-wrap justify-between items-center gap-3">
        <FilterDropdown
          title="Статус"
          value={statusFilter}
          options={STATUS_OPTIONS}
          onChange={setStatusFilter}
        />
        <Input
          value={searchQuery}
          onChange={setSearchQuery}
          placeholder="Введите ФИО или название организации"
          leftIcon={<Icon name="search-outline" size={16} />}
          className="w-full sm:w-80"
        />
      </div>

      <UsersTable
        users={filteredUsers}
        loading={isLoading}
        onRowClick={handleRowClick}
        onApprove={handleApprove}
        onReject={handleReject}
      />
    </div>
  );
}
